import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useNavigate, useLocation } from 'react-router-dom';
import PdfPreview from '../../../../Common/PDFPreview';
import { RingLoader } from 'react-spinners';
import { Typography } from '@mui/material';
import { formatReadableAmount } from "../../../../Common/FormatFunctions/FormatAmount";
import { FormaDateBalanceSheet } from "../../../../Common/FormatFunctions/FormatDate"

const apikey = process.env.REACT_APP_API;

const MillWisePurchaseDispatch = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');

    const companyCode = sessionStorage.getItem('Company_Code');
    const Year_Code = sessionStorage.getItem('Year_Code');
    const Company_Name = sessionStorage.getItem('Company_Name');
    const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pdfPreview, setPdfPreview] = useState(null);

    const API_URL = `${apikey}/MillWisePurchaseDispatch-Register`;

    useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get(API_URL, {
                    params: {
                        fromDT: fromDate,
                        toDT: toDate,
                        Company_Code: companyCode,
                        Year_Code: Year_Code,
                    },
                });
                setReportData(response.data);
            } catch (error) {
                console.error('Error fetching report:', error);
                setError('Error fetching report');
            } finally {
                setLoading(false);
            }
        };

        if (fromDate && toDate) {
            fetchReportData();
        }
    }, [fromDate, toDate, companyCode, Year_Code]);

    const handlePrint = () => {
        const printContent = document.getElementById('reportTable').outerHTML;
        const win = window.open('', '', 'height=700,width=900');
        win.document.write(`
            <html>
                <head>
                    <title>Print Report</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            margin: 20px;
                        }
                        .company-name {
                            text-align: center;
                            font-size: 24px;
                            font-weight: bold;
                            margin-bottom: 20px;
                            color: #333;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 20px;
                        }
                        th, td {
                            border: 1px solid #ddd;
                            padding: 8px;
                            text-align: left;
                        }
                        th {
                            background-color: #f2f2f2;
                            font-weight: bold;
                        }
                        tr:nth-child(even) {
                            background-color: #f9f9f9;
                        }
                        .total-row {
                            background-color: #e0f7fa;
                            font-weight: bold;
                        }
                    </style>
                </head>
                <body>
                    <div class="company-name">${Company_Name}</div>
                    ${printContent}
                </body>
            </html>
        `);
        win.document.close();
        win.print();
    };

    const generatePdf = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const textWidth = doc.getTextWidth(Company_Name);
        const xPosition = (pageWidth - textWidth) / 2;

        doc.text(Company_Name, xPosition, 10);
        doc.autoTable({ html: '#reportTable' });

        const pdfBlob = doc.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        setPdfPreview(url);
    };

    const handleExportToExcel = () => {
        const wb = XLSX.utils.book_new();
        const wsData = [];

        wsData.push([Company_Name]);
        wsData.push([]);

        wsData.push([
            'Doc No', 'Date', 'Qunatal', 'Mill Rate', 'Amount', 'TDS Amount',
            'CGST Amount', 'SGST Amount', 'IGST Amount', 'Bill Amount',
            'TCS Amount', 'Net Payable', 'Narration'
        ]);

        Object.entries(groupedReportData).forEach(([groupKey, { items }]) => {
            wsData.push([groupKey]);

            let totalQty = 0;
            let totalAmount = 0;
            let totalTDAmt = 0;
            let totalCGSTAmt = 0;
            let totalSGSTAmt = 0;
            let totalIGSTAmt = 0;
            let totalBillAmt = 0;
            let totalTCsAmt = 0;
            let totalNetAmt = 0;

            items.forEach(item => {
                const quantal = parseFloat(item.NETQNTL || 0);
                const rate = parseFloat(item.subTotal / item.NETQNTL || 0);
                const amount = parseFloat(item.Quantal * rate || 0);

                totalQty += quantal;
                totalAmount += amount;
                totalTDAmt += parseFloat(item.TDS_Amt || 0);
                totalCGSTAmt += parseFloat(item.CGSTAmount || 0);
                totalSGSTAmt += parseFloat(item.SGSTAmount || 0);
                totalIGSTAmt += parseFloat(item.IGSTAmount || 0);
                totalBillAmt += parseFloat(item.Bill_Amount || 0);
                totalTCsAmt += parseFloat(item.TCS_Amt || 0);
                totalNetAmt += parseFloat(item.TCS_Net_Payable || 0);

                wsData.push([
                    item.doc_no,
                    item.doc_dateConverted,
                    item.NETQNTL,
                    rate.toFixed(2),
                    amount.toFixed(2),
                    item.TDS_Amt,
                    item.CGSTAmount,
                    item.SGSTAmount,
                    item.IGSTAmount,
                    item.Bill_Amount,
                    item.TCS_Amt,
                    item.TCS_Net_Payable,
                    `${item.LORRYNO || ''} - ${item.SupplierShortname || ''}`
                ]);
            });

            wsData.push([
                '', '', totalQty.toFixed(2), '', totalAmount.toFixed(2),
                totalTDAmt.toFixed(2), totalCGSTAmt.toFixed(2), totalSGSTAmt.toFixed(2),
                totalIGSTAmt.toFixed(2), totalBillAmt.toFixed(2), totalTCsAmt.toFixed(2),
                totalNetAmt.toFixed(2), ''
            ]);
            wsData.push([]);
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, 'MillWiseDispatch');

        XLSX.writeFile(wb, 'MillWiseDispatch.xlsx');
    };


    const groupReportData = (data) => {
        if (!Array.isArray(data)) return {};
        const grouped = {};
        data.forEach(item => {
            const key = `${item.mill_code}-${item.millname}`;
            if (!grouped[key]) {
                grouped[key] = { items: [] };
            }
            grouped[key].items.push(item);
        });
        return grouped;
    };

    const groupedReportData = groupReportData(reportData);

    return (
        <div style={{ marginTop: '-80px' }}>
            {/* <Typography variant="h6" style={{ textAlign: 'center', fontSize: "24px", fontWeight: "bold" }}>{Company_Name}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", textDecoration: 'underline', fontWeight: "550" }}>GSTN : {Company_GSTNO}</Typography>
         */}

            <div className="d-flex justify-content-between align-items-center">
                <div style={{ flex: 1, textAlign: 'center', marginLeft: "280px" }}>
                    <Typography variant="h6" style={{ textAlign: 'center', fontSize: "20px", fontWeight: "bold" }}>Mill Wise Purchase</Typography>
                    <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px" }}>{FormaDateBalanceSheet(fromDate)} to {FormaDateBalanceSheet(toDate)}</Typography>
                </div>

                <div className="d-flex justify-content-end">
                    <button className="btn btn-secondary me-2" onClick={handlePrint}>Print Report</button>
                    <button className="btn btn-success me-2" onClick={handleExportToExcel}>Export to Excel</button>
                </div>
            </div>

            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                    <RingLoader />
                </div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : (
                <div style={{ maxHeight: "800px", overflowY: "auto" }}>
                    <table className="table table-striped table-bordered mt-4" id="reportTable" style={{ marginBottom: "60px", width: "100%" }}>
                        <thead className="table-light" style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1, whiteSpace: "nowrap" }}>
                            <tr>
                                <th>No</th>
                                <th>Date</th>
                                <th>Qunatal</th>
                                <th>Mill Rate</th>
                                <th>Amount</th>
                                <th>TDS Amount</th>
                                <th>CGST Amount</th>
                                <th>SGST Amount</th>
                                <th>IGST Amount</th>
                                <th>Bill Amount</th>
                                <th>TCS Amount</th>
                                <th>Net Payable</th>
                                <th>Narration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(groupedReportData).map(([key, { items }]) => {
                                const totalQty = items.reduce((sum, item) => sum + parseFloat(item.NETQNTL || 0), 0);
                                const totalAmount = items.reduce((sum, item) => sum + parseFloat(item.Quantal * (item.subTotal / item.NETQNTL) || 0), 0);
                                const totalTDAmt = items.reduce((sum, item) => sum + parseFloat(item.TDS_Amt || 0), 0);
                                const totalTCsAmt = items.reduce((sum, item) => sum + parseFloat(item.TCS_Amt || 0), 0);
                                const totalCGSTAmt = items.reduce((sum, item) => sum + parseFloat(item.CGSTAmount || 0), 0);
                                const totalSGSTAmt = items.reduce((sum, item) => sum + parseFloat(item.SGSTAmount || 0), 0);
                                const totalIGSTAmt = items.reduce((sum, item) => sum + parseFloat(item.IGSTAmount || 0), 0);
                                const totalBillAmt = items.reduce((sum, item) => sum + parseFloat(item.Bill_Amount || 0), 0);
                                const totalNetAmt = items.reduce((sum, item) => sum + parseFloat(item.TCS_Net_Payable || 0), 0);

                                return (
                                    <React.Fragment key={key}>
                                        <tr>
                                            <td colSpan={15} align="left" className="table-primary" style={{ color: 'blue', fontWeight: 'bold' }}>{key}</td>
                                        </tr>
                                        {items.map((item, index) => (
                                            <tr key={index}>
                                                <td style={{ fontWeight: 'bold' }} align='center'>{item.doc_no}</td>
                                                <td style={{ fontWeight: 'bold' }} align='center'>{item.doc_dateConverted}</td>
                                                <td align="right">{formatReadableAmount(item.NETQNTL)}</td>
                                                <td align="right">{(parseFloat(item.NETQNTL) > 0 && !isNaN(parseFloat(item.subTotal)))
                                                    ? (parseFloat(item.subTotal) / parseFloat(item.NETQNTL)).toFixed(2)
                                                    : '0.00'}</td>
                                                <td align="right">
                                                    {(() => {
                                                        const subTotal = Number(item.subTotal) || 0;
                                                        const netQntl = Number(item.NETQNTL) || 0;
                                                        const quantal = Number(item.Quantal) || 0;

                                                        if (netQntl !== 0) {
                                                            return (quantal * (subTotal / netQntl)).toFixed(2);
                                                        }
                                                        return '0.00';
                                                    })()}
                                                </td>
                                                <td align="right">{formatReadableAmount(item.TDS_Amt)}</td>
                                                <td align="right" style={{ fontWeight: 'bold' }}>{formatReadableAmount(item.CGSTAmount)}</td>
                                                <td align="right">{formatReadableAmount(item.SGSTAmount)}</td>
                                                <td align="right">{formatReadableAmount(item.IGSTAmount)}</td>
                                                <td align="right">{formatReadableAmount(item.Bill_Amount)}</td>
                                                <td align="right">{formatReadableAmount(item.TCS_Amt)}</td>
                                                <td align="right">{formatReadableAmount(item.TCS_Net_Payable)}</td>
                                                <td align="left">{`${item.LORRYNO || ''} - ${item.SupplierShortname || ''}`}</td>

                                            </tr>
                                        ))}
                                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f0f8ff' }}>
                                            <td colSpan={2} align="right">Total :</td>
                                            <td align="right">{formatReadableAmount(totalQty.toFixed(2))}</td>
                                            <td></td>
                                            <td align="right">{formatReadableAmount(totalAmount.toFixed(2))}</td>
                                            <td align="right">{formatReadableAmount(totalTDAmt.toFixed(2))}</td>
                                            <td align="right">{formatReadableAmount(totalCGSTAmt.toFixed(2))}</td>
                                            <td align="right">{formatReadableAmount(totalSGSTAmt.toFixed(2))}</td>
                                            <td align="right">{formatReadableAmount(totalIGSTAmt.toFixed(2))}</td>
                                            <td align="right">{formatReadableAmount(totalBillAmt.toFixed(2))}</td>
                                            <td align="right">{formatReadableAmount(totalTCsAmt.toFixed(2))}</td>
                                            <td align="right">{formatReadableAmount(totalNetAmt.toFixed(2))}</td>
                                        </tr>
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {pdfPreview && (
                <div className="centered-container">
                    <PdfPreview pdfData={pdfPreview} apiData={reportData} label={'NewDispatchRegister'} />
                </div>
            )}
        </div>
    );
};

export default MillWisePurchaseDispatch;